import React, { useState } from 'react';
import { CheckCircle, AlertTriangle, X, ChevronDown, ChevronUp, Shield, Clock, FileText, Pencil } from 'lucide-react';
import api from '../../api';
import toast from 'react-hot-toast';
import VerificationBadge, { VerificationMetadata } from './VerificationBadge';
import SourceTracePanel from './SourceTracePanel';

export type ClaimCorrectionScope = 'sop' | 'process_map';

interface VerificationPanelProps {
  metadata?: VerificationMetadata | null;
  onClose?: () => void;
  embedded?: boolean;
  correctionScope?: ClaimCorrectionScope;
  correctionScopeId?: number;
  onMetadataUpdated?: (nextMetadata: VerificationMetadata) => void;
}

const VerificationPanel: React.FC<VerificationPanelProps> = ({
  metadata,
  onClose,
  embedded = false,
  correctionScope,
  correctionScopeId,
  onMetadataUpdated
}) => {
  const [expandedClaims, setExpandedClaims] = useState<Set<number>>(new Set());
  const [activeTab, setActiveTab] = useState<'self_verification' | 'citation_trace'>('self_verification');
  const [editingClaimId, setEditingClaimId] = useState<string | null>(null);
  const [editDraft, setEditDraft] = useState<string>('');
  const [savingClaimId, setSavingClaimId] = useState<string | null>(null);

  const canEdit = Boolean(correctionScope && correctionScopeId && onMetadataUpdated);

  if (!metadata) {
    return (
      <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 text-sm text-gray-600">
        No verification data available.
      </div>
    );
  }

  if (metadata.skipped || !metadata.enabled) {
    const isDeferred = metadata.skipped_reason === 'verification_mode_off' || (metadata as any).deferred === true;
    return (
      <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 text-sm text-gray-600">
        <div className="flex items-center gap-2 font-medium text-gray-700 mb-1">
          <Shield className="w-4 h-4" />
          {isDeferred ? 'Verifier Not Run Yet' : 'Verification Skipped'}
        </div>
        <div className="text-xs text-gray-500">
          {isDeferred
            ? 'This output was generated with the verifier off (faster). Click Run Verifier in the toolbar to check the claims against source documents.'
            : (metadata.skipped_reason || 'Verification was not enabled for this output.')}
        </div>
      </div>
    );
  }

  const traces = metadata.traces || [];
  const claimsExtracted = metadata.claims_extracted ?? 0;
  const claimsVerified = metadata.claims_verified ?? 0;
  const claimsFlagged = metadata.claims_flagged ?? 0;
  const claimsCorrected = metadata.claims_corrected ?? 0;
  const failureRate = metadata.failure_rate ?? 0;
  const confidence = metadata.confidence_score ?? Math.round((1 - failureRate) * 100);
  const latencyMs = metadata.latency_ms ?? 0;

  const toggleClaim = (idx: number) => {
    const next = new Set(expandedClaims);
    if (next.has(idx)) next.delete(idx);
    else next.add(idx);
    setExpandedClaims(next);
  };

  const startEditing = (claimId: string, currentText: string) => {
    setEditingClaimId(claimId);
    setEditDraft(currentText);
  };

  const cancelEditing = () => {
    setEditingClaimId(null);
    setEditDraft('');
  };

  const saveCorrection = async (claimId: string, trust = false) => {
    if (!canEdit || !claimId) return;
    const trimmed = editDraft.trim();
    if (!trimmed) {
      toast.error('Correction cannot be empty');
      return;
    }
    setSavingClaimId(claimId);
    try {
      const res = await api.post(`/api/analysis/verification/claim/${claimId}/correct`, {
        scope: correctionScope,
        scope_id: correctionScopeId,
        corrected_text: trimmed,
        trust
      });
      const data = res.data || {};
      if (!metadata) return;
      const nextTraces = (metadata.traces || []).map(t => (t.id === claimId ? data.trace : t));
      const nextMetadata: VerificationMetadata = {
        ...metadata,
        ...(data.aggregate || {}),
        traces: nextTraces
      };
      if (onMetadataUpdated) onMetadataUpdated(nextMetadata);
      cancelEditing();
      if (data.warning) {
        toast(data.warning, { icon: '⚠️', duration: 5000 });
      } else if (trust) {
        toast.success('Saved as user-verified — confidence updated');
      } else if (data.trace?.verdict === 'supported') {
        toast.success('Correction saved and re-verified');
      } else {
        toast(`Correction saved. Re-check verdict: ${data.trace?.verdict || 'unknown'}. Click "Trust this edit" if you're sure it's correct.`, { icon: 'ℹ️', duration: 5000 });
      }
    } catch (err: any) {
      const msg = err?.response?.data?.error || err?.response?.data?.message || 'Failed to save correction';
      toast.error(msg);
    } finally {
      setSavingClaimId(null);
    }
  };

  const containerClass = embedded
    ? 'bg-white border border-gray-200 rounded-lg'
    : 'bg-white border border-gray-200 rounded-lg shadow-lg';

  const citation = (metadata as any).citation_verification || null;
  const hasCitation = citation && citation.status !== 'skipped' && citation.total_claims > 0;

  return (
    <div className={containerClass}>
      <div className="flex items-center justify-between p-4 border-b border-gray-200">
        <div className="flex items-center gap-3">
          <Shield className="w-5 h-5 text-indigo-600" />
          <div>
            <h3 className="text-sm font-semibold text-gray-900">VeriFact-CoT Verification</h3>
            <p className="text-xs text-gray-500">Mode: {metadata.mode || 'standard'} · Module: {metadata.module || 'unknown'}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <VerificationBadge metadata={metadata} size="md" />
          {onClose && (
            <button onClick={onClose} className="p-1 rounded hover:bg-gray-100">
              <X className="w-4 h-4 text-gray-500" />
            </button>
          )}
        </div>
      </div>

      {hasCitation && (
        <div className="flex border-b border-gray-200 bg-gray-50">
          <button
            onClick={() => setActiveTab('self_verification')}
            className={`flex-1 px-4 py-2 text-xs font-medium border-b-2 ${
              activeTab === 'self_verification'
                ? 'border-indigo-600 text-indigo-700'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            <span className="flex items-center justify-center gap-1.5">
              <Shield className="w-3 h-3" />
              Self-Verification ({metadata.claims_extracted ?? 0})
            </span>
          </button>
          <button
            onClick={() => setActiveTab('citation_trace')}
            className={`flex-1 px-4 py-2 text-xs font-medium border-b-2 ${
              activeTab === 'citation_trace'
                ? 'border-indigo-600 text-indigo-700'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            <span className="flex items-center justify-center gap-1.5">
              <FileText className="w-3 h-3" />
              Source Trace ({citation.total_claims ?? 0})
            </span>
          </button>
        </div>
      )}

      {hasCitation && activeTab === 'citation_trace' ? (
        <SourceTracePanel citation={citation} embedded />
      ) : (
      <>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 p-4 bg-gray-50 border-b border-gray-200">
        <div>
          <div className="text-xs text-gray-500">Claims Extracted</div>
          <div className="text-lg font-semibold text-gray-900">{claimsExtracted}</div>
        </div>
        <div>
          <div className="text-xs text-gray-500">Verified</div>
          <div className="text-lg font-semibold text-green-700">{claimsVerified}</div>
        </div>
        <div>
          <div className="text-xs text-gray-500">Flagged</div>
          <div className="text-lg font-semibold text-red-700">{claimsFlagged}</div>
        </div>
        <div>
          <div className="text-xs text-gray-500">Confidence</div>
          <div className="text-lg font-semibold text-indigo-700">{confidence}%</div>
        </div>
      </div>

      {claimsCorrected > 0 && (
        <div className="px-4 py-2 bg-blue-50 border-b border-blue-200 text-xs text-blue-800">
          {claimsCorrected} claim(s) were corrected via deep verification.
        </div>
      )}

      {(metadata as any).claims_user_verified > 0 && (
        <div className="px-4 py-2 bg-purple-50 border-b border-purple-200 text-xs text-purple-800">
          {(metadata as any).claims_user_verified} claim(s) marked user-verified — counted toward confidence.
        </div>
      )}

      {metadata.needs_human_review && (
        <div className="px-4 py-2 bg-red-50 border-b border-red-200 text-xs text-red-800 flex items-center gap-2">
          <AlertTriangle className="w-4 h-4" />
          Failure rate {(failureRate * 100).toFixed(0)}% exceeds threshold — human review recommended before relying on this output.
        </div>
      )}

      <div className="max-h-96 overflow-y-auto">
        {traces.length === 0 ? (
          <div className="p-4 text-sm text-gray-500 text-center">No claim traces recorded.</div>
        ) : (
          <ul className="divide-y divide-gray-100">
            {traces.map((trace, i) => {
              const isFlagged = trace.status === 'flagged';
              const isCorrected = trace.status === 'corrected';
              const isVerified = trace.status === 'verified';
              const isUserVerified = trace.status === 'user_verified';
              const expanded = expandedClaims.has(i);
              const claimId = trace.id || '';
              const isEditing = canEdit && claimId !== '' && editingClaimId === claimId;
              const isSaving = savingClaimId === claimId;
              const editable = canEdit && claimId !== '' && (isFlagged || isUserVerified || trace.verdict === 'partially_supported' || trace.verdict === 'uncertain' || trace.user_corrected);
              const sourceLabel = trace.source?.hint
                ? trace.source.hint
                : (trace.source?.quote ? trace.source.quote.slice(0, 80) + (trace.source.quote.length > 80 ? '…' : '') : null);

              return (
                <li key={claimId || i} className="p-3">
                  <div className="flex items-start gap-2">
                    <button
                      onClick={() => toggleClaim(i)}
                      className="flex-1 min-w-0 flex items-start gap-2 text-left"
                    >
                      <div className="flex-shrink-0 mt-0.5">
                        {isVerified && <CheckCircle className="w-4 h-4 text-green-600" />}
                        {isFlagged && <AlertTriangle className="w-4 h-4 text-red-600" />}
                        {isCorrected && <CheckCircle className="w-4 h-4 text-blue-600" />}
                        {isUserVerified && <CheckCircle className="w-4 h-4 text-purple-600" />}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-xs text-gray-900 leading-snug">{trace.claim}</div>
                        <div className="flex items-center gap-2 mt-1 text-[10px] text-gray-500 flex-wrap">
                          {trace.category && <span className="px-1.5 py-0.5 bg-gray-100 rounded">{trace.category}</span>}
                          {typeof trace.confidence === 'number' && <span>{trace.confidence}% conf</span>}
                          {trace.verdict && <span className="capitalize">{trace.verdict.replace('_', ' ')}</span>}
                          {isUserVerified && <span className="px-1.5 py-0.5 rounded bg-purple-50 text-purple-700 border border-purple-100 font-medium">User-verified</span>}
                          {trace.user_corrected && !isUserVerified && <span className="px-1.5 py-0.5 rounded bg-indigo-50 text-indigo-700 border border-indigo-100">Edited by you</span>}
                        </div>
                      </div>
                    </button>
                    {editable && !isEditing && (
                      <button
                        type="button"
                        onClick={() => startEditing(claimId, trace.claim)}
                        title="Edit this claim"
                        className="flex-shrink-0 p-1 rounded text-gray-400 hover:bg-gray-100 hover:text-indigo-600"
                      >
                        <Pencil className="w-3.5 h-3.5" />
                      </button>
                    )}
                    <button onClick={() => toggleClaim(i)} className="flex-shrink-0 p-1">
                      {expanded ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
                    </button>
                  </div>

                  {isEditing && (
                    <div className="mt-2 ml-6 pl-3 border-l-2 border-indigo-200 space-y-2 text-xs">
                      <textarea
                        value={editDraft}
                        onChange={e => setEditDraft(e.target.value)}
                        rows={3}
                        maxLength={4000}
                        className="w-full p-2 border border-gray-300 rounded text-xs text-gray-900 focus:border-indigo-500 focus:outline-none"
                        placeholder="Write the corrected claim text..."
                        disabled={isSaving}
                      />
                      {sourceLabel && (
                        <div className="text-[10px] text-gray-500">
                          Updates: <span className="font-medium text-gray-700">{sourceLabel}</span>
                        </div>
                      )}
                      <div className="flex items-center gap-2 flex-wrap">
                        <button
                          type="button"
                          onClick={() => saveCorrection(claimId, false)}
                          disabled={isSaving || !editDraft.trim()}
                          className="px-3 py-1 rounded text-[11px] font-medium bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-50 flex items-center gap-1.5"
                        >
                          {isSaving && <div className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin" />}
                          {isSaving ? 'Saving...' : 'Save & re-verify'}
                        </button>
                        <button
                          type="button"
                          onClick={() => saveCorrection(claimId, true)}
                          disabled={isSaving || !editDraft.trim()}
                          title="Mark as user-verified without LLM re-check. Use when you're confident the corrected claim is accurate even if the source documents don't explicitly support it."
                          className="px-3 py-1 rounded text-[11px] font-medium bg-purple-600 text-white hover:bg-purple-700 disabled:opacity-50"
                        >
                          Trust this edit
                        </button>
                        <button
                          type="button"
                          onClick={cancelEditing}
                          disabled={isSaving}
                          className="px-3 py-1 rounded text-[11px] font-medium border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  )}

                  {expanded && !isEditing && (
                    <div className="mt-2 ml-6 pl-3 border-l-2 border-gray-200 space-y-2 text-xs">
                      {trace.question && (
                        <div>
                          <div className="text-gray-500">Verification question:</div>
                          <div className="text-gray-800">{trace.question}</div>
                        </div>
                      )}
                      {trace.answer && (
                        <div>
                          <div className="text-gray-500">Independent answer:</div>
                          <div className="text-gray-800">{trace.answer}</div>
                        </div>
                      )}
                      {trace.evidence && (
                        <div>
                          <div className="text-gray-500">Evidence:</div>
                          <div className="text-gray-800 italic">"{trace.evidence}"</div>
                        </div>
                      )}
                      {trace.correction && (
                        <div>
                          <div className="text-blue-600">Correction:</div>
                          <div className="text-gray-800">{trace.correction}</div>
                          {trace.correction_reason && <div className="text-gray-500 text-[10px]">Reason: {trace.correction_reason}</div>}
                        </div>
                      )}
                      {Array.isArray(trace.history) && trace.history.length > 0 && (
                        <div>
                          <div className="text-gray-500">Edit history:</div>
                          <ul className="space-y-1 mt-1">
                            {trace.history.map((h, hi) => (
                              <li key={hi} className="text-gray-700">
                                <div className="text-[10px] text-gray-500">
                                  {new Date(h.at).toLocaleString()} · re-check: <span className="capitalize">{(h.reverification_verdict || '').replace('_', ' ') || 'unknown'}</span>
                                  {h.content_patched === false && <span className="ml-1 text-amber-600">(SOP/map text not updated)</span>}
                                </div>
                                <div className="text-[11px]">"{h.previous_text}" → "{h.new_text}"</div>
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </div>

      <div className="flex items-center justify-between px-4 py-2 border-t border-gray-200 text-[10px] text-gray-500">
        <span className="flex items-center gap-1">
          <Clock className="w-3 h-3" />
          {latencyMs}ms
        </span>
        {metadata.trace_id && <span className="font-mono">{metadata.trace_id.slice(-12)}</span>}
      </div>
      </>
      )}
    </div>
  );
};

export default VerificationPanel;
